import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearAuth } from '../../utils/auth';
import apiConfig, { API_ENDPOINTS } from '../../config/api';

// Queue for handling concurrent refresh token requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Create an Axios instance with authentication interceptors
 */
export const createAuthenticatedClient = (config?: AxiosRequestConfig): AxiosInstance => {
  const client = axios.create({
    baseURL: apiConfig.baseURL,
    timeout: 1200000, // 20 minutes for large file operations
    ...config,
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle token refresh
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = error.config;
      // A 401 from login means wrong credentials, not an expired session —
      // redirecting to /login would reload the page and wipe the error message.
      const isLoginRequest = originalRequest?.url?.endsWith(API_ENDPOINTS.AUTH.LOGIN);

      if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return client(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          const refreshToken = getRefreshToken();
          if (!refreshToken) {
            isRefreshing = false;
            clearAuth();
            window.location.href = '/login';
            reject(new Error('No refresh token available'));
            return;
          }

          axios
            .post(`${apiConfig.baseURL}${API_ENDPOINTS.AUTH.REFRESH}`, {
              access_token: getAccessToken(),
              refresh_token: refreshToken
            })
            .then(({ data }) => {
              const { access_token, refresh_token: newRefreshToken } = data;
              if (access_token && newRefreshToken) {
                setTokens(access_token, newRefreshToken);
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${access_token}`;
                }
                processQueue(null, access_token);
                resolve(client(originalRequest));
              } else {
                throw new Error('Invalid refresh response');
              }
            })
            .catch((err) => {
              processQueue(err, null);
              clearAuth();
              window.location.href = '/login';
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Default authenticated API client instance
 */
export const apiClient = createAuthenticatedClient();

/**
 * Create a client without authentication (for public endpoints)
 */
export const createPublicClient = (config?: AxiosRequestConfig): AxiosInstance => {
  return axios.create({
    baseURL: apiConfig.baseURL,
    timeout: 1200000, // 20 minutes for large file operations
    ...config,
  });
};

export default apiClient;